import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Button from "../../components/Button";
import TextInput from "../../components/TextInput";
import { images } from "../../config/images";
import { EMAIL, PASSWORD_VERY_STRONG, testRegex } from "../../config/regex";
import {
  requestPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPasswordWithOtp,
} from "../../api";
import Swal from "sweetalert2";
import { Eye, EyeOff } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [step, setStep] = useState("request"); // request | verify | reset | done
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validateField = (field, value, otherValue) => {
    const v = String(value ?? "").trim();
    switch (field) {
      case "email":
        if (!v) return "Email address is required";
        if (!testRegex(EMAIL, v)) return "Enter a valid email address";
        return "";
      case "newPassword":
        if (!v) return "Password is required";
        if (v.length < 8)
          return "Password must be 8 chars with 1 letter & 1 number & 1 special char";
        if (!testRegex(PASSWORD_VERY_STRONG, v))
          return "Must include uppercase, lowercase, number, and special character";
        return "";
      case "confirmPassword":
        if (!v) return "Confirmation is required";
        if (v !== (otherValue ?? newPassword)) return "Passwords do not match";
        return "";
      default:
        return "";
    }
  };

  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val);
    setFieldErrors((prev) => ({ ...prev, email: validateField("email", val) }));
    setError("");
  };

  const handlePasswordChange = (e) => {
    const val = e.target.value;
    setNewPassword(val);
    const passErr = validateField("newPassword", val);

    // If confirm password already has a value, re-validate it too
    let confirmErr = fieldErrors.confirmPassword;
    if (confirmPassword) {
      confirmErr = validateField("confirmPassword", confirmPassword, val);
    }

    setFieldErrors((prev) => ({
      ...prev,
      newPassword: passErr,
      confirmPassword: confirmErr,
    }));
    setError("");
  };

  const handleConfirmPasswordChange = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setFieldErrors((prev) => ({
      ...prev,
      confirmPassword: validateField("confirmPassword", val),
    }));
    setError("");
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    const emailErr = validateField("email", email);
    if (emailErr) {
      setFieldErrors((prev) => ({ ...prev, email: emailErr }));
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await requestPasswordResetOtp(email.trim());
      if (!res.success) {
        const msg = res.message || "Failed to send OTP. Please try again.";
        setError(msg);
        Swal.fire({
          icon: "error",
          title: "Unable to send OTP",
          text: msg,
          confirmButtonColor: "#38915c",
        });
      } else {
        setStep("verify");
        Swal.fire({
          icon: "success",
          title: "OTP sent",
          text: res.message || "OTP sent to your email address",
          confirmButtonColor: "#38915c",
        });
      }
    } catch (err) {
      const msg = "Failed to send OTP. Please try again.";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: msg,
        confirmButtonColor: "#38915c",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const emailErr = validateField("email", email);
    if (emailErr) {
      setFieldErrors((prev) => ({ ...prev, email: emailErr }));
      return;
    }
    if (!otp.trim()) {
      setError("Please enter OTP");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await verifyPasswordResetOtp(email.trim(), otp.trim());
      if (!res.success || !res.resetToken) {
        const msg = res.message || "Invalid OTP. Please try again.";
        setError(msg);
        Swal.fire({
          icon: "error",
          title: "OTP verification failed",
          text: msg,
          confirmButtonColor: "#38915c",
        });
        return;
      }
      setResetToken(res.resetToken);
      setStep("reset");
      Swal.fire({
        icon: "success",
        title: "OTP verified",
        text: "Now set your new password.",
        confirmButtonColor: "#38915c",
      });
    } catch {
      const msg = "Failed to verify OTP. Please try again.";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: msg,
        confirmButtonColor: "#38915c",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    const passErr = validateField("newPassword", newPassword);
    const confirmErr = validateField("confirmPassword", confirmPassword);

    if (passErr || confirmErr) {
      setFieldErrors({ newPassword: passErr, confirmPassword: confirmErr });
      setError("Please fix the highlighted fields.");
      return;
    }

    if (!resetToken) {
      setError("Please verify OTP first.");
      setStep("verify");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await resetPasswordWithOtp(
        resetToken,
        newPassword,
        confirmPassword,
      );
      if (!res.success) {
        const msg = res.message || "Failed to reset password";
        setError(msg);
        Swal.fire({
          icon: "error",
          title: "Reset failed",
          text: msg,
          confirmButtonColor: "#38915c",
        });
        return;
      }
      setStep("done");
      Swal.fire({
        icon: "success",
        title: "Password updated",
        text: res.message || "Your password has been updated successfully.",
        confirmButtonColor: "#38915c",
      });
    } catch {
      const msg = "Failed to reset password. Please try again.";
      setError(msg);
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        text: msg,
        confirmButtonColor: "#38915c",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 py-12 lg:py-20 4xl:py-24">
        <div className="w-full max-w-6xl 4xl:max-w-11xl grid lg:grid-cols-2 gap-8 lg:gap-12 4xl:gap-32 items-center">
          {/* Left Side - Branding */}
          <div className="hidden lg:flex flex-col items-center justify-center relative">
            <div className="relative w-full max-w-lg 4xl:max-w-3xl">
              <div className="flex items-center gap-4 4xl:gap-8 mb-8 4xl:mb-16">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-16 4xl:h-28 w-auto"
                />
                <div>
                  <h1 className="text-4xl whitespace-nowrap 4xl:text-7xl font-bold text-primary-600">
                    Market Mavericks
                  </h1>
                  <p className="text-gray-600 text-sm 4xl:text-2xl">
                    Your Gateway to Stock Market Success
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-2xl p-8 4xl:p-16 border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary-200 rounded-full -mr-16 -mt-16 opacity-20" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-secondary-200 rounded-full -ml-12 -mb-12 opacity-20" />
                <div className="relative z-10">
                  <h3 className="text-2xl 4xl:text-5xl font-bold text-gray-800 mb-2">
                    Reset your password
                  </h3>
                  <p className="text-gray-600 4xl:text-2xl">
                    Enter your email to receive an OTP, then verify and set a
                    new password.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Forgot Password Form */}
          <div className="w-full max-w-md 4xl:max-w-2xl mx-auto lg:mx-0">
            <div className="bg-white rounded-2xl shadow-2xl p-8 lg:p-10 4xl:p-20 border border-gray-100">
              <div className="lg:hidden flex items-center gap-3 mb-6">
                <img
                  src={images.logo}
                  alt="Market Mavericks Logo"
                  className="h-12 w-auto"
                />
                <h2 className="text-2xl font-bold text-primary-600">
                  Market Mavericks
                </h2>
              </div>

              <div className="mb-8 4xl:mb-16">
                <h2 className="text-3xl 4xl:text-6xl font-bold text-gray-900 mb-2">
                  Forgot Password?
                </h2>
                <p className="text-gray-600 4xl:text-2xl">
                  {step === "request"
                    ? "Enter your email to receive OTP"
                    : step === "verify"
                      ? "Enter the OTP sent to your email"
                      : step === "reset"
                        ? "Set your new password"
                        : "Password updated successfully"}
                </p>
              </div>

              {step === "done" ? (
                <div className="space-y-4 4xl:space-y-8">
                  <div className="p-4 rounded-lg bg-primary-50 text-primary-700 text-sm 4xl:text-xl border border-primary-200">
                    Your password has been updated. You can now sign in with
                    your new password.
                  </div>
                  <Link to="/login" className="block">
                    <Button variant="primary" className="w-full !py-3">
                      Back to Sign In
                    </Button>
                  </Link>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm 4xl:text-xl">
                      {error}
                    </div>
                  )}

                  {step === "request" && (
                    <form
                      onSubmit={handleRequestOtp}
                      className="space-y-6 4xl:space-y-12"
                    >
                      <TextInput
                        name="email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="Enter your email"
                        error={fieldErrors.email}
                        required
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-full"
                        disabled={loading}
                      >
                        {loading ? "Sending..." : "Send OTP"}
                      </Button>
                    </form>
                  )}

                  {step === "verify" && (
                    <form
                      onSubmit={handleVerifyOtp}
                      className="space-y-6 4xl:space-y-12"
                    >
                      <TextInput
                        name="email"
                        label="Email Address"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="Enter your email"
                        error={fieldErrors.email}
                        required
                      />
                      <TextInput
                        name="otp"
                        label="OTP"
                        type="text"
                        value={otp}
                        onChange={(e) => {
                          setOtp(e.target.value);
                          setError("");
                        }}
                        placeholder="Enter 6-digit OTP"
                        required
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 4xl:gap-6">
                        <Button
                          type="button"
                          variant="secondary"
                          className="w-full"
                          disabled={loading}
                          onClick={() => {
                            setStep("request");
                            setOtp("");
                            setError("");
                          }}
                        >
                          Change email
                        </Button>
                        <Button
                          type="submit"
                          variant="primary"
                          className="w-full"
                          disabled={loading}
                        >
                          {loading ? "Verifying..." : "Verify OTP"}
                        </Button>
                      </div>
                    </form>
                  )}

                  {step === "reset" && (
                    <form
                      onSubmit={handleResetPassword}
                      className="space-y-6 4xl:space-y-12"
                    >
                      <TextInput
                        name="newPassword"
                        label="New Password"
                        type={showPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Enter new password"
                        error={fieldErrors.newPassword}
                        required
                        icon={
                          <button
                            type="button"
                            onClick={() => setShowPassword((p) => !p)}
                            className="text-gray-400 hover:text-primary-500 transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        }
                        iconPosition="right"
                      />
                      <TextInput
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        placeholder="Confirm new password"
                        error={fieldErrors.confirmPassword}
                        required
                        icon={
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((p) => !p)}
                            className="text-gray-400 hover:text-primary-500 transition-colors"
                          >
                            {showConfirmPassword ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        }
                        iconPosition="right"
                      />
                      <Button
                        type="submit"
                        variant="primary"
                        className="w-full !py-3"
                        disabled={loading}
                      >
                        {loading ? "Updating..." : "Update Password"}
                      </Button>
                    </form>
                  )}
                </>
              )}

              <p className="mt-6 4xl:mt-12 text-center text-gray-600 4xl:text-2xl">
                Remember your password?{" "}
                <Link
                  to="/login"
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Sign in
                </Link>
              </p>

              <div className="mt-4 4xl:mt-8 text-center">
                <Link
                  to="/"
                  className="text-sm 4xl:text-xl text-gray-500 hover:text-gray-700"
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
